import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Storefront, Upload, Trash, Hash, ChatCircleDots, ShieldCheck, BellRinging, CreditCard, MapPin,
  FloppyDisk, ArrowCounterClockwise, ListChecks, LockSimple, Printer,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { clearCompanyCache } from '@/lib/company';
import BranchesCard from '@/components/BranchesCard';
import { RazorpaySettingsCard } from '@/components/RazorpaySettingsCard';
import { KeyboardShortcutsButton } from '@/components/KeyboardShortcuts';
import { SettingsSection, SettingsNav, FieldLabel, Hint } from '@/components/SettingsLayout';

const DEFAULT_INTIMATION = {
  submitted: 'Dear {customer_name},\n\nYour application for *{service_name}* (Invoice {invoice_number}) has been *Submitted* on {date}.\n\nWe\'ll keep you updated.\n\nThank you!',
  approved: 'Dear {customer_name},\n\nGood news! Your application for *{service_name}* (Invoice {invoice_number}) has been *Approved* on {date}.\n\nPlease collect the documents from our office.\n\nThank you!',
  under_process: 'Dear {customer_name},\n\nYour application for *{service_name}* (Invoice {invoice_number}) is currently *Under Process* as on {date}.\n\nWe\'ll inform you as soon as there is an update.\n\nThank you!',
  docs_pending: 'Dear {customer_name},\n\nYour application for *{service_name}* (Invoice {invoice_number}) is on hold — *Documents Pending* as on {date}.\n\nKindly share the required documents at the earliest so we can proceed.\n\nThank you!',
};

const SECTIONS = [
  { id: 'company-profile', title: 'Company Profile', icon: Storefront },
  { id: 'invoice-numbering', title: 'Invoice Numbering', icon: Hash },
  { id: 'whatsapp', title: 'WhatsApp Messaging', icon: ChatCircleDots },
  { id: 'staff-policies', title: 'Staff Policies', icon: ShieldCheck },
  { id: 'intimation-templates', title: 'Status Intimations', icon: BellRinging },
  { id: 'online-payments', title: 'Online Payments', icon: CreditCard },
  { id: 'branches', title: 'Branches', icon: MapPin },
];

const WA_TOKENS = ['{customer_name}', '{invoice_number}', '{total}', '{due}', '{paid}', '{date}', '{center}', '{services}'];
const WA_BLOCKS = [
  { open: '{if_due}', close: '{/if_due}', label: '{if_due}…{/if_due}', hint: 'Render only when the invoice still has an outstanding balance' },
  { open: '{if_paid}', close: '{/if_paid}', label: '{if_paid}…{/if_paid}', hint: 'Render only when the invoice is fully settled' },
];
const WA_SAMPLE = { customer_name: 'Anand Kumar', invoice_number: 'INV-00042', total: '1,250', due: '450', paid: '800', date: new Date().toLocaleDateString('en-IN'), services: 'PAN Card, Aadhaar Update' };

const inputCls = 'rounded-sm border-[#E4E4E7] bg-white focus-visible:ring-0 focus-visible:border-[#0A0A0A] mt-1.5';

function normalize(r) {
  return {
    owner_name: r.owner_name || '', owner_email: r.owner_email || '', owner_mobile: r.owner_mobile || '',
    name: r.name || '', address: r.address || '', mobile: r.mobile || '', email: r.email || '', logo: r.logo || '',
    whatsapp_number: r.whatsapp_number || '', whatsapp_template: r.whatsapp_template || '',
    invoice_prefix: r.invoice_prefix ?? 'INV-', invoice_start_number: r.invoice_start_number || 1,
    intimation_templates: {
      submitted: r.intimation_templates?.submitted || '', approved: r.intimation_templates?.approved || '',
      under_process: r.intimation_templates?.under_process || '', docs_pending: r.intimation_templates?.docs_pending || '',
    },
    expense_approval_threshold: r.expense_approval_threshold ?? 500,
    late_deduction_policy: r.late_deduction_policy || '',
  };
}

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const waRef = useRef(null);

  useEffect(() => {
    api.get('/settings/company')
      .then(r => { const f = normalize(r.data); setForm(f); setInitial(f); })
      .catch(console.error);
  }, []);

  const dirty = useMemo(() => form && initial && JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image file'); return; }
    if (file.size > 1.5 * 1024 * 1024) { toast.error('Image too large (max ~1.5 MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = img.width > 400 ? 400 / img.width : 1;
        canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL(file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85);
        if (dataUrl.length > 800_000) { toast.error('Logo too large after compression. Pick a smaller image.'); return; }
        setForm(f => ({ ...f, logo: dataUrl }));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = useCallback(async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      await api.put('/settings/company', { ...form, invoice_start_number: Number(form.invoice_start_number) || 1 });
      clearCompanyCache();
      window.dispatchEvent(new CustomEvent('company-updated'));
      setInitial(form);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [form, saving]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (dirty) handleSave(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, handleSave]);

  const insertAtCursor = (text) => {
    const el = waRef.current;
    setForm(f => {
      const cur = f.whatsapp_template || '';
      const start = el?.selectionStart ?? cur.length, end = el?.selectionEnd ?? cur.length;
      const next = cur.slice(0, start) + text + cur.slice(end);
      requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + text.length, start + text.length); } });
      return { ...f, whatsapp_template: next };
    });
  };

  const waPreview = useMemo(() => {
    if (!form) return '';
    let t = form.whatsapp_template || 'Hello {customer_name},\n\nYour bill {invoice_number} of ₹{total} from {center} has an outstanding balance of ₹{due}. Kindly arrange the payment at your earliest.\n\nThank you!';
    t = t.replace(/\{if_due\}([\s\S]*?)\{\/if_due\}/g, '$1').replace(/\{if_paid\}[\s\S]*?\{\/if_paid\}/g, '');
    const vals = { ...WA_SAMPLE, center: form.name || 'Your Centre' };
    return Object.entries(vals).reduce((s, [k, v]) => s.replaceAll(`{${k}}`, v), t);
  }, [form]);

  const auditVouchers = async () => {
    try {
      const r = await api.post('/settings/audit-vouchers');
      const { updated, remaining_without_voucher } = r.data || {};
      toast.success(`Voucher audit complete — ${updated} row${updated === 1 ? '' : 's'} stamped, ${remaining_without_voucher} still without voucher`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    }
  };

  if (!form) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  const invoicePreview = `${(form.invoice_prefix || '').trim()}${String(Math.max(1, Number(form.invoice_start_number) || 1)).padStart(5, '0')}`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-28" data-testid="settings-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <p className="text-xs text-[#52525B] max-w-xl">Manage company identity, invoice numbering, messaging templates, staff policies and payment integrations. Changes apply to everyone in your centre.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" variant="outline" onClick={auditVouchers} className="h-9 rounded-sm text-xs border-[#E4E4E7] hover:bg-[#0A0A0A] hover:text-white" data-testid="audit-vouchers-btn" title="Stamp parent invoice numbers on any finance rows that are missing a voucher">
            <ListChecks size={14} className="mr-1.5" /> Re-audit Voucher Numbers
          </Button>
          <KeyboardShortcutsButton />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <SettingsNav sections={SECTIONS} />
        </aside>

        <div className="lg:col-span-9 space-y-6">
          {/* ── Company Profile ─────────────────────────────────────── */}
          <SettingsSection id="company-profile" icon={Storefront} title="Company Profile" description="Business identity and contact details. Public fields are printed on every invoice.">
            <div className="grid grid-cols-1 md:grid-cols-[9rem_1fr] gap-6">
              <div className="flex flex-col items-center gap-2">
                <label className="group/logo relative w-36 h-36 rounded-sm border border-dashed border-[#D4D4D8] bg-[#FAFAFA] flex items-center justify-center overflow-hidden cursor-pointer hover:border-[#0A0A0A] transition-colors" data-testid="company-logo-preview">
                  <input type="file" accept="image/png,image/jpeg" onChange={handleLogoPick} className="hidden" data-testid="company-logo-input" />
                  {form.logo ? <img src={form.logo} alt="logo" className="w-full h-full object-contain p-2" /> : (
                    <span className="flex flex-col items-center gap-1 text-[#A1A1AA]"><Upload size={18} /><span className="text-[10px] uppercase tracking-[0.15em]">Upload logo</span></span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-[#0A0A0A]/85 text-white text-[10px] uppercase tracking-[0.15em] text-center py-1 opacity-0 group-hover/logo:opacity-100 transition-opacity">{form.logo ? 'Change' : 'Browse'}</span>
                </label>
                {form.logo && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, logo: '' }))} className="inline-flex items-center gap-1 text-[11px] text-[#EF4444] hover:underline" data-testid="company-logo-remove">
                    <Trash size={12} /> Remove
                  </button>
                )}
                <p className="text-[10px] text-[#A1A1AA] text-center">PNG/JPG · max 1.5 MB</p>
              </div>

              <div className="space-y-5">
                <div>
                  <FieldLabel hint="appears as the invoice header">Company Name</FieldLabel>
                  <Input value={form.name} onChange={set('name')} placeholder="e.g. Akshaya CSC Centre" className={`${inputCls} text-base font-semibold`} data-testid="company-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel>Business Mobile</FieldLabel>
                    <Input value={form.mobile} onChange={set('mobile')} placeholder="+91 9XXXXXXXXX" className={inputCls} data-testid="company-mobile-input" />
                  </div>
                  <div>
                    <FieldLabel>Business Email</FieldLabel>
                    <Input type="email" value={form.email} onChange={set('email')} placeholder="contact@example.com" className={inputCls} data-testid="company-email-input" />
                  </div>
                </div>
                <div>
                  <FieldLabel>Address</FieldLabel>
                  <Textarea value={form.address} onChange={set('address')} rows={2} placeholder="Street, City, State – PIN" className={`${inputCls} resize-none`} data-testid="company-address-input" />
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-dashed border-[#E4E4E7]">
              <div className="flex items-center gap-2 mb-3">
                <LockSimple size={13} className="text-[#52525B]" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#52525B]">Owner (private)</span>
                <span className="text-[10px] text-[#A1A1AA]">— never printed on invoices</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <FieldLabel>Owner Name</FieldLabel>
                  <Input value={form.owner_name} onChange={set('owner_name')} placeholder="Full name of proprietor" className={inputCls} data-testid="owner-name-input" />
                </div>
                <div>
                  <FieldLabel>Owner Mobile</FieldLabel>
                  <Input value={form.owner_mobile} onChange={set('owner_mobile')} placeholder="+91 9XXXXXXXXX" className={inputCls} data-testid="owner-mobile-input" />
                </div>
                <div>
                  <FieldLabel>Owner Email</FieldLabel>
                  <Input type="email" value={form.owner_email} onChange={set('owner_email')} placeholder="owner@example.com" className={inputCls} data-testid="owner-email-input" />
                </div>
              </div>
            </div>
          </SettingsSection>

          {/* ── Invoice Numbering ───────────────────────────────────── */}
          <SettingsSection id="invoice-numbering" icon={Hash} title="Invoice Numbering" description="Prefix + 5-digit running number. New invoices continue from the last number issued under this prefix, or from the starting number — whichever is higher.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <FieldLabel>Prefix</FieldLabel>
                <Input value={form.invoice_prefix} onChange={set('invoice_prefix')} placeholder="INV-" maxLength={20} className={`${inputCls} font-mono`} data-testid="invoice-prefix-input" />
                <Hint>e.g. CSC/26-27/ · changing it starts a fresh sequence</Hint>
              </div>
              <div>
                <FieldLabel>Starting Number</FieldLabel>
                <Input type="number" min={1} value={form.invoice_start_number} onChange={set('invoice_start_number')} className={`${inputCls} font-mono`} data-testid="invoice-start-number-input" />
                <Hint>Used when no invoice exists yet under this prefix</Hint>
              </div>
              <div className="rounded-sm bg-[#0A0A0A] text-white px-4 py-3 self-start mt-5">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/60"><Printer size={11} /> Next invoice</div>
                <div className="font-mono font-bold text-lg mt-1 whitespace-nowrap overflow-hidden text-ellipsis" data-testid="invoice-number-preview">{invoicePreview}</div>
              </div>
            </div>
          </SettingsSection>

          {/* ── WhatsApp Messaging ──────────────────────────────────── */}
          <SettingsSection id="whatsapp" icon={ChatCircleDots} title="WhatsApp Messaging" description="Used by the green WhatsApp icon beside each customer in Invoice Management.">
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_18rem] gap-6">
              <div className="space-y-5">
                <div>
                  <FieldLabel hint='shown as "Reply to …" and used when a customer has no phone'>Default WhatsApp Number</FieldLabel>
                  <Input value={form.whatsapp_number} onChange={set('whatsapp_number')} placeholder="919XXXXXXXXX (country code + number, digits only)" className={`${inputCls} font-mono`} data-testid="company-whatsapp-input" />
                </div>
                <div>
                  <FieldLabel hint="click a chip to insert at the cursor">Bill Message Template</FieldLabel>
                  <div className="flex flex-wrap gap-1.5 mt-2 mb-2" data-testid="whatsapp-template-placeholders">
                    {WA_TOKENS.map(tok => (
                      <button type="button" key={tok} onClick={() => insertAtCursor(tok)} title={`Insert ${tok}`}
                        className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[#E4E4E7] bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                        data-testid={`whatsapp-token-${tok.replace(/[{}]/g, '')}`}>{tok}</button>
                    ))}
                    {WA_BLOCKS.map(blk => (
                      <button type="button" key={blk.open} onClick={() => insertAtCursor(blk.open + blk.close)} title={blk.hint}
                        className="text-[10px] font-mono px-2 py-0.5 rounded-sm border border-[#F59E0B]/40 bg-[#FFFBEB] text-[#92400E] hover:bg-[#F59E0B] hover:text-white transition-colors"
                        data-testid={`whatsapp-block-${blk.open.replace(/[{}/]/g, '')}`}>{blk.label}</button>
                    ))}
                  </div>
                  <Textarea ref={waRef} value={form.whatsapp_template} onChange={set('whatsapp_template')} rows={7}
                    placeholder={'Hello {customer_name},\n\nYour bill {invoice_number} of ₹{total} from {center} has an outstanding balance of ₹{due}. Kindly arrange the payment at your earliest.\n\nThank you!'}
                    className={`${inputCls} text-sm font-mono leading-relaxed`} data-testid="whatsapp-template-textarea" />
                  <Hint>
                    Content inside <span className="font-mono text-[#92400E]">{'{if_due}…{/if_due}'}</span> renders only when the bill is unpaid, <span className="font-mono text-[#92400E]">{'{if_paid}…{/if_paid}'}</span> only when settled. Leave empty to use the built-in default.
                  </Hint>
                </div>
              </div>
              <div className="xl:mt-7" data-testid="whatsapp-preview">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#52525B] mb-2">Live preview</div>
                <div className="rounded-sm bg-[#ECE5DD] p-3 min-h-[12rem]">
                  <div className="relative bg-white rounded-md rounded-tl-none px-3 py-2 text-[12px] leading-relaxed text-[#111] whitespace-pre-wrap shadow-sm">
                    {waPreview}
                    <div className="text-[9px] text-[#8696A0] text-right mt-1">{new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
                <Hint>Sample values · unpaid bill scenario</Hint>
              </div>
            </div>
          </SettingsSection>

          {/* ── Staff Policies ──────────────────────────────────────── */}
          <SettingsSection id="staff-policies" icon={ShieldCheck} title="Staff Policies" description="Expense governance and attendance discipline rules shown to your team.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FieldLabel hint="staff entries above this need admin approval">Expense Approval Threshold</FieldLabel>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#52525B] font-mono">₹</span>
                  <Input type="number" min={0} value={form.expense_approval_threshold}
                    onChange={e => setForm(f => ({ ...f, expense_approval_threshold: parseFloat(e.target.value || 0) }))}
                    className="rounded-sm border-[#E4E4E7] bg-white font-mono pl-7 focus-visible:ring-0 focus-visible:border-[#0A0A0A]" data-testid="expense-threshold-input" />
                </div>
                <Hint>Default ₹500. Admin-created expenses are always auto-approved. Set 0 to require approval for every staff expense.</Hint>
              </div>
              <div>
                <FieldLabel hint="shown to staff on the Attendance page">Late Deduction Policy</FieldLabel>
                <Input value={form.late_deduction_policy} onChange={set('late_deduction_policy')} placeholder="e.g. 3 late entries → half-day deduction" className={inputCls} data-testid="late-deduction-policy-input" />
              </div>
            </div>
          </SettingsSection>

          {/* ── Intimation Templates ────────────────────────────────── */}
          <SettingsSection id="intimation-templates" icon={BellRinging} title="Status Intimations"
            description="Messages sent from the Follow-up page WhatsApp icon for each work status."
            action={(
              <Button type="button" variant="ghost" size="sm" className="h-8 rounded-sm text-xs text-[#52525B] hover:bg-[#0A0A0A] hover:text-white"
                onClick={() => setForm(f => ({ ...f, intimation_templates: { ...DEFAULT_INTIMATION } }))} data-testid="reset-intimation-templates-btn">
                <ArrowCounterClockwise size={13} className="mr-1" /> Reset to default
              </Button>
            )}>
            <p className="text-[11px] text-[#A1A1AA] mb-4">
              Placeholders: {['{customer_name}', '{service_name}', '{invoice_number}', '{date}', '{status}'].map(t => <code key={t} className="font-mono text-[#0A0A0A] bg-[#F4F4F5] px-1 py-0.5 rounded-sm mr-1">{t}</code>)}
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[
                { key: 'submitted', label: 'Submitted', dot: 'bg-[#F59E0B]' },
                { key: 'approved', label: 'Approved', dot: 'bg-[#10B981]' },
                { key: 'under_process', label: 'Under Process', dot: 'bg-[#3B82F6]' },
                { key: 'docs_pending', label: 'Docs. Pending', dot: 'bg-[#EF4444]' },
              ].map(t => (
                <div key={t.key} className="rounded-sm border border-[#E4E4E7] p-3 hover:border-[#0A0A0A] transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`h-2 w-2 rounded-full ${t.dot}`} />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#0A0A0A]">{t.label}</span>
                  </div>
                  <Textarea value={form.intimation_templates[t.key] || ''}
                    onChange={e => setForm(f => ({ ...f, intimation_templates: { ...f.intimation_templates, [t.key]: e.target.value } }))}
                    rows={6} className="rounded-sm border-[#E4E4E7] text-xs font-mono leading-relaxed resize-none bg-[#FAFAFA] focus-visible:ring-0 focus-visible:border-[#0A0A0A] focus:bg-white"
                    data-testid={`intimation-template-${t.key}`} placeholder={`Message sent when the item is ${t.label}`} />
                </div>
              ))}
            </div>
          </SettingsSection>

          {/* ── Online Payments / Branches (independent components) ─── */}
          <section id="online-payments" className="scroll-mt-24" data-testid="settings-section-online-payments"><RazorpaySettingsCard /></section>
          <section id="branches" className="scroll-mt-24" data-testid="settings-section-branches"><BranchesCard /></section>
        </div>
      </div>

      {/* ── Sticky save bar ───────────────────────────────────────── */}
      <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-40 transition-all duration-200 ${dirty ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none invisible'}`} data-testid="settings-save-bar">
        <div className="flex items-center gap-3 rounded-sm border border-[#E4E4E7] bg-white/95 backdrop-blur-md shadow-xl px-4 py-2.5">
          <span className="flex items-center gap-2 text-xs text-[#52525B]"><span className="h-2 w-2 rounded-full bg-[#F59E0B] animate-pulse" /> Unsaved changes</span>
          <Button type="button" variant="ghost" size="sm" className="h-8 rounded-sm text-xs hover:bg-[#F4F4F5]" onClick={() => setForm(initial)} disabled={saving} data-testid="discard-settings-btn">Discard</Button>
          <Button type="button" size="sm" onClick={handleSave} disabled={saving} className="h-8 rounded-sm bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white text-xs font-semibold" data-testid="save-company-btn">
            <FloppyDisk size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Settings'}
            <kbd className="ml-2 hidden sm:inline text-[9px] font-mono bg-white/15 rounded-sm px-1 py-0.5">Ctrl+S</kbd>
          </Button>
        </div>
      </div>
    </div>
  );
}
